import * as React from 'react';

import useModalAccessibility from '../../../../Shared/hooks/useModalAccessibility';
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor';
import {
  EXTRA_SMALL_ICON_BASE_SRC,
  Tooltip,
  OneThumbSlider,
  Snackbar,
  defaultSnackbarTimeout,
} from '../../../../Shared/index';

const helpImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`;
const arrowRightImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/arrow-right.svg`;

const MomentumScoreGoalModal = ({ closeModal, momentumScoreGoal, setMomentumScoreGoal, isAdminContext = false, }) => {
  const { modalRef } = useModalAccessibility(closeModal);

  const [newMomentumScoreGoal, setNewMomentumScoreGoal] = React.useState(momentumScoreGoal)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarText, setSnackbarText] = React.useState('')

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  function isValidMomentumScore(value: string | number): boolean {
    const numValue = Number(value)
    return !isNaN(numValue) && numValue >= 50 && numValue <= 100
  }

  function showValidationSnackbar(): void {
    setSnackbarText('The Momentum Score goal must be a number between 50 and 100.')
    setShowSnackbar(true)
  }

  function handleClickSaveGoal(): void {
    if (!isValidMomentumScore(newMomentumScoreGoal)) {
      showValidationSnackbar()
      return
    }

    setMomentumScoreGoal(newMomentumScoreGoal)
    closeModal('Goal saved')
  }

  function handleChangeNewMomentumScoreGoalInput(e: React.ChangeEvent<HTMLInputElement>): void {
    setNewMomentumScoreGoal(e.target.value)
  }

  function handleChangeNewMomentumScoreGoalSlider(valuesArray: number[]): void {
    setNewMomentumScoreGoal(valuesArray[0])
  }

  return (
    <div className="modal-container momentum-score-goal-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="momentumScoreGoalModal"
        aria-modal="true"
        className="momentum-score-goal-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="title">
          <h2 id="momentumScoreGoalModal">Set Momentum Score Goal</h2>
          <Tooltip
            tooltipText="This goal sets the minimum Momentum Score students must achieve to be placed in the 'Meeting Goal' Smart Group. To be eligible, students must also have medium or high practice exposure. Your Smart Groups will update dynamically when you save a new goal."
            tooltipTriggerText={<img alt="Help icon" src={helpImgSrc} />}
          />
        </div>
        <p>Students are organized into Smart Groups dynamically based on your goal. Only students with sufficient practice are eligible to meet the goal.{isAdminContext && ' This setting only affects Smart Groups in the admin report. Teachers can set their own goal.'}</p>
        <div className="form-elements">
          <div className="inputs">
            <div>
              <label htmlFor="currentGoal" id="currentGoalLabel">Current Goal</label>
              <input aria-labelledby="currentGoalLabel" disabled={true} id="currentGoal" value={momentumScoreGoal} />
            </div>
            <img alt="" src={arrowRightImgSrc} />
            <div>
              <label htmlFor="newGoal" id="newGoalLabel">New Goal</label>
              <input aria-labelledby="newGoalLabel" id="newGoal" max={100} min={50} onChange={handleChangeNewMomentumScoreGoalInput} value={newMomentumScoreGoal} />
            </div>
          </div>
          <div className="sliders-container">
            <OneThumbSlider
              defaultValue={newMomentumScoreGoal}
              handleChange={handleChangeNewMomentumScoreGoalSlider}
              markLabels={[50, 60, 70, 80, 90, 100]}
              maxValue={100}
              minValue={50}
              step={1}
              value={newMomentumScoreGoal}
            />
          </div>
        </div>
        <div className="form-buttons">
          <button className="quill-button outlined large grey focus-on-light" onClick={closeModal} type="button">Cancel</button>
          <button className="quill-button contained large green focus-on-light" disabled={newMomentumScoreGoal === momentumScoreGoal} onClick={handleClickSaveGoal} type="button">Save goal</button>
        </div>
      </div>
      <Snackbar text={snackbarText} visible={showSnackbar} />
    </div>
  )
}

export default MomentumScoreGoalModal
